import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import Footer from '../components/Footer';
import StatCounter from '../components/StatCounter';


const inputStyle = {
  width:'100%', padding:'10px 14px',
  border:'1px solid var(--border-2)', borderRadius:'4px',
  fontFamily:'inherit', fontSize:'14px', color:'var(--text-h)',
  background:'#fff', outline:'none', transition:'border 0.15s',
};

export default function ContactPage({ onNavigate }) {
  const [form, setForm] = useState({ fname:'', lname:'', email:'', company:'', service:'', module:'', query:'' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.id]: e.target.value }));

  /*
    EMAILJS SETUP — do this once at https://emailjs.com (free):
    1. Sign up and go to Email Services → Add Service → Gmail
    2. Connect webdynellc@gmail.com and copy the Service ID
    3. Go to Email Templates → Create Template with these variables:
         {{from_name}}, {{from_email}}, {{company}},
         {{service}}, {{d365_module}}, {{message}}
       Set "To Email" in the template to: webdynellc@gmail.com
    4. Copy the Template ID
    5. Go to Account → Copy your Public Key
    6. Replace YOUR_SERVICE_ID, YOUR_TEMPLATE_ID, YOUR_PUBLIC_KEY above
  */
  const handleSubmit = async () => {
    const required = ['fname', 'email', 'service', 'query'];
    const newErrors = {};
    required.forEach(f => { if (!form[f].trim()) newErrors[f] = true; });
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});

    try {
      await emailjs.send(
        'service_4cqgquh',
        'template_v7s0cbz',
        {
          from_name:    `${form.fname} ${form.lname}`,
          from_email:   form.email,
          company:      form.company || 'Not provided',
          service:      form.service,
          d365_module:  form.module  || 'Not specified',
          message:      form.query,
          to_email:     'webdynellc@gmail.com',
        },
        's6yuvJkZyKVrQ7a9o'
      );
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      alert('Sorry, there was an error sending your message. Please try again.');
    }
  };

  const fieldBorder = (id) => errors[id] ? '1px solid var(--ms-red)' : '1px solid var(--border-2)';

  return (
    <div>
      <Helmet>
        <title>Contact AdjustD365 — Hire a Dynamics 365 Consultant</title>
        <meta name="description" content="Get in touch with AdjustD365 to discuss your Dynamics 365 requirement. Fixed-price quotes, 24-hour response time, 8+ years D365 experience." />
        <link rel="canonical" href="https://adjustd365.com/contact" />
      </Helmet>
      {/* PAGE HERO */}
      <div className="section-pad" style={{ background:'var(--bg-2)', borderBottom:'1px solid var(--border)', padding:'56px 48px' }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto' }}>
          <div style={{ fontSize:'13px', color:'var(--text-s)', marginBottom:'16px', display:'flex', alignItems:'center', gap:'6px' }}>
            <button onClick={() => onNavigate('home')} style={{ background:'none', border:'none', color:'var(--text-s)', cursor:'pointer', fontSize:'13px' }}>Home</button>
            <span>›</span>
            <span style={{ color:'var(--ms-blue)', fontWeight:500 }}>Contact</span>
          </div>
          <h1 style={{ fontSize:'clamp(26px,3vw,42px)', fontWeight:700, letterSpacing:'-0.02em', color:'var(--text-h)', marginBottom:'12px' }}>Get in Touch</h1>
          <p style={{ fontSize:'16px', color:'var(--text-m)', maxWidth:'580px' }}>
            Tell me about your D365 project or challenge. I'll review your query and get back to you within 24 hours.
          </p>
        </div>
      </div>

      {/* FORM SECTION */}
      <div className="section-pad" style={{ padding:'72px 48px' }}>
        <div style={{ maxWidth:'760px', margin:'0 auto' }}>
          <p style={{ fontSize:'15px', color:'var(--text-m)', marginBottom:'36px', lineHeight:1.7 }}>
            Whether you need a one-off customisation, an ongoing support arrangement, or you're not yet sure what you need — fill in the form below and I'll come back with a straightforward assessment.
          </p>

          <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:'8px', padding:'40px' }}>

            {/* Name row */}
            <div className="grid-2col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
              <div>
                <label htmlFor="fname" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>
                  First Name <span style={{ color:'var(--ms-blue)' }}>*</span>
                </label>
                <input id="fname" type="text" placeholder="e.g. James" value={form.fname} onChange={handleChange} style={{ ...inputStyle, border:fieldBorder('fname') }} />
              </div>
              <div>
                <label htmlFor="lname" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>Last Name</label>
                <input id="lname" type="text" placeholder="e.g. Thompson" value={form.lname} onChange={handleChange} style={inputStyle} />
              </div>
            </div>

            {/* Email + Company */}
            <div className="grid-2col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
              <div>
                <label htmlFor="email" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>
                  Business Email <span style={{ color:'var(--ms-blue)' }}>*</span>
                </label>
                <input id="email" type="email" placeholder="you@company.com" value={form.email} onChange={handleChange} style={{ ...inputStyle, border:fieldBorder('email') }} />
              </div>
              <div>
                <label htmlFor="company" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>Company Name</label>
                <input id="company" type="text" placeholder="Optional" value={form.company} onChange={handleChange} style={inputStyle} />
              </div>
            </div>

            {/* Service */}
            <div style={{ marginBottom:'20px' }}>
              <label htmlFor="service" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>
                Service Required <span style={{ color:'var(--ms-blue)' }}>*</span>
              </label>
              <select id="service" value={form.service} onChange={handleChange} style={{ ...inputStyle, border:fieldBorder('service'), backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23605E5C' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 14px center', paddingRight:'36px', appearance:'none' }}>
                <option value="">— Select a service —</option>
                <option>Plugin Development</option>
                <option>Web Resources & Scripting</option>
                <option>PCF Control Development</option>
                <option>PVA Chatbot Development</option>
                <option>Power Automate & Workflows</option>
                <option>Data Migration & Import</option>
                <option>CRM Support & Maintenance</option>
                <option>Third-Party Integration</option>
                <option>Not sure — need advice</option>
              </select>
            </div>

            {/* D365 Module */}
            <div style={{ marginBottom:'20px' }}>
              <label htmlFor="module" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>D365 Module / Environment</label>
              <input id="module" type="text" placeholder="e.g. D365 CE Sales, Finance & Ops 10.0, Power Platform Online" value={form.module} onChange={handleChange} style={inputStyle} />
              <div style={{ fontSize:'12px', color:'var(--text-s)', marginTop:'5px' }}>Helps me give you a more accurate response.</div>
            </div>

            {/* Query */}
            <div style={{ marginBottom:'0' }}>
              <label htmlFor="query" style={{ display:'block', fontSize:'13px', fontWeight:600, color:'var(--text-h)', marginBottom:'6px' }}>
                Tell me about your project <span style={{ color:'var(--ms-blue)' }}>*</span>
              </label>
              <textarea
                id="query"
                rows={5}
                placeholder="Describe what you need built or fixed — include the business outcome you're aiming for, your timeline, and any relevant constraints or budget range."
                value={form.query}
                onChange={handleChange}
                style={{ ...inputStyle, resize:'vertical', lineHeight:1.6, border:fieldBorder('query') }}
              />
            </div>

            {/* Submit row */}
            {!submitted ? (
              <div className="submit-row" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'28px', paddingTop:'24px', borderTop:'1px solid var(--border)' }}>
                <span style={{ fontSize:'13px', color:'var(--text-s)' }}>I respond to every query within 24 hours.</span>
                <button
                  onClick={handleSubmit}
                  style={{ background:'var(--ms-blue)', color:'#fff', padding:'12px 32px', borderRadius:'4px', fontSize:'14px', fontWeight:600, border:'none', cursor:'pointer' }}
                  onMouseEnter={e => e.target.style.background = 'var(--ms-blue-dark)'}
                  onMouseLeave={e => e.target.style.background = 'var(--ms-blue)'}
                >
                  Submit Query
                </button>
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'center', gap:'12px', background:'#DFF6D7', border:'1px solid #107C10', borderRadius:'6px', padding:'20px 24px', marginTop:'24px', color:'#0B5A0B', fontSize:'14px', fontWeight:500 }}>
                ✓ &nbsp;Thank you — your query has been received. I'll be in touch within 24 hours.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* METRICS BAND */}
      <div className="section-pad" style={{ background:'var(--bg-2)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'40px 48px' }}>
        <div className="metrics-row" style={{ maxWidth:'1200px', margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'center' }}>
          {[
            { target:96, suffix:'+', staticVal:null, label:'Happy Customers' },
            { target:0,  suffix:'',  staticVal:'★ 5.0', label:'Client Rating' },
            { target:24, suffix:'h', staticVal:null, label:'Response Time', prefix:'< ' },
            { target:8,  suffix:'+ yrs', staticVal:null, label:'D365 Experience' },
          ].map((m, i, arr) => (
            <div key={i} style={{ flex:1, padding:'24px', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <StatCounter
                target={m.target}
                prefix={m.prefix || ''}
                suffix={m.suffix}
                staticVal={m.staticVal}
                label={m.label}
                duration={1600}
                numStyle={{ fontSize:'30px', letterSpacing:'-0.02em' }}
                labStyle={{ fontSize:'13px', color:'var(--text-m)', marginTop:'4px' }}
              />
            </div>
          ))}
        </div>
      </div>

      <Footer onNavigate={onNavigate} />
    </div>
  );
}
